import useThemeColors from '@/hooks/useThemeColors';
import { Calendar, Download } from 'lucide-react';
import React from 'react';

interface Props {
    onClose: () => void;
    subscriptionToken: string;
}

const SubscribeModal: React.FC<Props> = ({ onClose, subscriptionToken }) => {
    const colors = useThemeColors();
    const subscribeUrl = `${window.location.origin}/calendars/subscribe/${subscriptionToken}/`;
    const webcalUrl = subscribeUrl.replace(/^https?:/, 'webcal:');

    // Generate URLs for different calendar services
    // Google Calendar - using the working format from www.google.com/calendar/render with webcal
    const googleCalendarUrl = `https://www.google.com/calendar/render?cid=${webcalUrl}`;
    const outlookUrl = `https://outlook.live.com/calendar/0/addfromweb?url=${encodeURIComponent(subscribeUrl)}&name=ConquerX`;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
            onClick={onClose}
        >
            <div
                className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="mb-4 flex items-start justify-between">
                    <h3 className="text-2xl font-bold text-gray-900">
                        Suscribirse al Calendario
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 transition-colors hover:text-gray-600"
                    >
                        <svg
                            className="h-6 w-6"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                    </button>
                </div>

                <div className="space-y-4">
                    <p className="text-gray-700">
                        Selecciona tu aplicación de calendario para suscribirte automáticamente:
                    </p>

                    {/* Calendar Service Buttons */}
                    <div className="space-y-3">
                        {/* Google Calendar */}
                        <a
                            href={googleCalendarUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex w-full items-center gap-3 rounded-lg border border-gray-300 bg-white p-4 text-left transition-colors hover:bg-gray-50"
                        >
                            <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${colors.iconBg}`}>
                                <Calendar className={`h-5 w-5 ${colors.iconColor}`} />
                            </div>
                            <div className="flex-1">
                                <p className="font-semibold text-gray-900">Google Calendar</p>
                                <p className="text-sm text-gray-500">Suscribirse con un clic</p>
                            </div>
                            <svg
                                className="h-5 w-5 text-gray-400"
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path d="M9 5l7 7-7 7"></path>
                            </svg>
                        </a>

                        {/* Outlook */}
                        <a
                            href={outlookUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex w-full items-center gap-3 rounded-lg border border-gray-300 bg-white p-4 text-left transition-colors hover:bg-gray-50"
                        >
                            <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${colors.iconBg}`}>
                                <Calendar className={`h-5 w-5 ${colors.iconColor}`} />
                            </div>
                            <div className="flex-1">
                                <p className="font-semibold text-gray-900">Outlook</p>
                                <p className="text-sm text-gray-500">Suscribirse con un clic</p>
                            </div>
                            <svg
                                className="h-5 w-5 text-gray-400"
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path d="M9 5l7 7-7 7"></path>
                            </svg>
                        </a>

                        {/* Apple Calendar */}
                        <a
                            href={webcalUrl}
                            className="flex w-full items-center gap-3 rounded-lg border border-gray-300 bg-white p-4 text-left transition-colors hover:bg-gray-50"
                        >
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100">
                                <Calendar className="h-5 w-5 text-gray-700" />
                            </div>
                            <div className="flex-1">
                                <p className="font-semibold text-gray-900">Apple Calendar</p>
                                <p className="text-sm text-gray-500">Abrir en Calendar app</p>
                            </div>
                            <svg
                                className="h-5 w-5 text-gray-400"
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path d="M9 5l7 7-7 7"></path>
                            </svg>
                        </a>

                        {/* Download ICS */}
                        <a
                            href={subscribeUrl}
                            className="flex w-full items-center gap-3 rounded-lg border border-gray-300 bg-white p-4 text-left transition-colors hover:bg-gray-50"
                        >
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100">
                                <Download className="h-5 w-5 text-green-600" />
                            </div>
                            <div className="flex-1">
                                <p className="font-semibold text-gray-900">Descargar archivo .ics</p>
                                <p className="text-sm text-gray-500">
                                    Para otras aplicaciones de calendario
                                </p>
                            </div>
                            <svg
                                className="h-5 w-5 text-gray-400"
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path d="M9 5l7 7-7 7"></path>
                            </svg>
                        </a>
                    </div>
                </div>

                <div className="mt-6 flex justify-end">
                    <button
                        onClick={onClose}
                        className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
                    >
                        Cerrar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SubscribeModal;
